// Step 4 — the columns one attribute set asks for (prototype `workSet`): a
// designer-style column grid, Add column, Copy columns from another set, and
// "How it looks" — the set drawn by the runtime's own form renderer, inert.

import { useState } from 'react'
import { Check, Copy, GripVertical, Plus, Search, ShieldCheck, Zap } from 'lucide-react'
import { tw } from '../../testHooks'
import { whyCaption } from '../merge'
import {
  clone, formOf, isStandard, newColumn, ORIGIN_TENANT, setPreviewModel, takenKeys, truthy, whenText,
  WHEN_ALWAYS,
} from './model'
import { opSet, useSetup } from './state'
import PreviewForm from './PreviewForm'
import { OriginPills } from './ui'

export default function StepColumns() {
  const { forms, formNo, draft, node, go, labelOf, notify, additionsAllowed, setTab, objName, project, mode } = useSetup()
  const form = formOf(draft.layer, formNo)
  const setup = forms.find(f => f.formNo === formNo) || null
  const setKey = node.t === 'set' || node.t === 'col' ? node.set : ''
  const set = form?.sets.find(s => s.key === setKey)
  const [copyOpen, setCopyOpen] = useState(false)
  if (!form || !set) return <div className="uds-na">Pick an attribute set in the tree.</div>
  const lab = labelOf(formNo)
  const when = whenText(form.drivers, set, lab)
  const record = setup?.isHeader ? 'record' : 'line'

  const addCol = () => {
    if (!additionsAllowed(formNo)) { notify('info', 'The application does not allow adding columns here.'); return }
    const col = newColumn(takenKeys(form, setup))
    draft.edit(opSet(formNo, set.key, s => { s.columns.push(col) }))
    go({ t: 'col', set: set.key, col: col.name })
    setTab('general')
  }
  const copyFrom = (fromKey: string) => {
    const from = form.sets.find(s => s.key === fromKey)
    if (!from) return
    // Every column of a form has its own key, so a copy gets a new one.
    const taken = takenKeys(form, setup)
    const copies = (from.columns || []).filter(c => !c.retired).map(c => {
      const y = clone(c)
      let name = `${c.name}_copy`
      let n = 2
      while (taken.includes(name)) name = `${c.name}_copy${n++}`
      taken.push(name)
      y.name = name
      y.origin = ORIGIN_TENANT
      delete y.tenant_may
      return y
    })
    draft.edit(opSet(formNo, set.key, s => { s.columns.push(...copies) }))
    setCopyOpen(false)
    notify('success', `Copied ${copies.length} column${copies.length === 1 ? '' : 's'}. Each copy can be changed on its own.`)
  }
  const values: Record<string, unknown> = {}
  for (const c of set.columns || []) if (c.default_value) values[`uda_${c.name}`] = c.default_value
  const group = set.placement?.group || set.label || set.key
  const caption = when === WHEN_ALWAYS ? '' : whyCaption(when)

  return (
    <>
      <div className="uds-lead">
        <h2>4. Columns for &ldquo;{set.label || set.key}&rdquo;</h2>
        <p>Asked when {when === WHEN_ALWAYS ? 'any value, on every ' + record : when}. Select a column to set its properties, the same
          way as in the transaction designer.</p>
      </div>
      <div className="uds-card">
        <div className="uds-card-h">
          <h3>Columns</h3><span className="uds-grow" />
          <button type="button" className="btn btn-secondary btn-sm" disabled={form.sets.length < 2} onClick={() => setCopyOpen(o => !o)}>
            <Copy size={14} /> Copy columns from…
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={addCol} {...tw('action', 'uda-add-column')}>
            <Plus size={14} /> Add column
          </button>
        </div>
        <div className="uds-tblwrap">
          <table className="uds-tbl">
            <thead><tr><th /><th>Column name</th><th>Heading</th><th>Type</th><th className="c">Mandatory</th><th>Default</th><th>Behaviour</th><th /></tr></thead>
            <tbody>
              {(set.columns || []).map(c => {
                const sel = node.t === 'col' && node.col === c.name
                const pub = draft.published.has(`${formNo}|${c.name}`)
                return (
                  <tr key={c.name} className={`click ${sel ? 'sel' : ''}`} onClick={() => go({ t: 'col', set: set.key, col: c.name })}
                    {...tw('row', c.name)}>
                    <td style={{ color: 'var(--color-text-faint)' }}><GripVertical size={15} /></td>
                    <td className="uds-mono">{c.name}</td>
                    <td>{c.heading}{c.retired && <> <span className="uds-pill uds-p-std">Retired</span></>}</td>
                    <td className="uds-mono">{c.col_type || 'VARCHAR'}{c.db_size ? `(${c.db_size})` : ''}</td>
                    <td className="c">{truthy(c.mandatory) ? <Check size={15} /> : ''}</td>
                    <td className="uds-mono" style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.default_value}</td>
                    <td>
                      <span className="uds-icons">
                        {c.lookup && <span title="Lookup"><Search size={14} /></span>}
                        {c.item_change && <span title="When it changes"><Zap size={14} /></span>}
                        {!!(c.validations || []).length && <span title="Validations"><ShieldCheck size={14} /></span>}
                      </span>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <OriginPills standard={isStandard(c)} published={mode === 'tenant' ? pub : null} />
                    </td>
                  </tr>
                )
              })}
              {!(set.columns || []).length && <tr><td colSpan={8} className="uds-na">No columns yet. Add the first one.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {copyOpen && (
        <div className="uds-card">
          <div className="uds-card-h"><h3>Copy columns from another combination</h3></div>
          <div className="uds-card-b uds-chips">
            {form.sets.filter(s => s.key !== set.key).map(s => (
              <button key={s.key} type="button" className="btn btn-secondary btn-sm" onClick={() => copyFrom(s.key)}>
                {s.label || s.key} · {(s.columns || []).length}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="uds-card">
        <div className="uds-card-h"><h3>How it looks</h3><p>The group people see on the {record} for this combination.</p></div>
        <div className="uds-card-b" style={{ background: 'var(--color-bg-secondary)' }}>
          <div className="uds-rgrp">
            <div className="uds-rgrp-h"><h3>{set.label || set.key}</h3><span className="why">{when}</span></div>
            <div style={{ padding: '4px 14px 12px' }}>
              <PreviewForm model={setPreviewModel(set, setup?.title || '', group)} values={values}
                project={project} objName={objName} formNo={formNo}
                captions={caption ? { [group]: caption } : undefined} />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
